// Application-wide constants

export const ANILIST_API_URL = 'https://graphql.anilist.co';

// Proxy URL: routes AniList GraphQL through our backend to avoid CORS.
// In dev (port 3000), hit the local server on :5000; in production, use relative /api path.
const API_BASE = typeof window !== 'undefined' && window.location.port === '3000'
    ? 'http://localhost:5000/api'
    : '/api';
export const ANILIST_PROXY_URL = `${API_BASE}/anilist`;

export const OP_ED_SKIP_MINUTES = 2.5;

export const LOCAL_STORAGE_KEYS = {
    TOKEN: 'anilist_token',
    USERNAME: 'anilist_username',
    USER_DATA: 'anilist_user_data',
};

export const CACHE_TTL = {
    USER_DATA: 24 * 60 * 60 * 1000, // 24 hours
};


// OAuth Configuration (PIN-based flow)
export const OAUTH_CONFIG = {
    CLIENT_ID: '19457',
    REDIRECT_URI: 'https://anilist.co/api/v2/oauth/pin',
    AUTHORIZE_URL: 'https://anilist.co/api/v2/oauth/authorize',
    RESPONSE_TYPE: 'token' // 'token' gives access token directly on PIN page
};


// ===== Master Tab Configuration =====
export const MASTER_TABS = [
    { id: 'anime', name: '🎬 Anime', path: '/anime' },
    { id: 'manga', name: '📖 Manga', path: '/manga' },
    { id: 'settings', name: '⚙️ Settings', path: '/settings' },
];

// Anime sub-tabs (under /anime)
export const ANIME_TABS = [
    { id: 'dashboard', name: '🏠 Overview', path: '/anime/dashboard' },
    { id: 'groups', name: '📚 Collections', path: '/anime/groups' },
    { id: 'related', name: '🔗 Related Anime', path: '/anime/related' },
    { id: 'discover', name: '🔍 Search', path: '/anime/discover' },
    { id: 'marathon', name: '⏳ Marathon Mode', path: '/anime/marathon' },
    { id: 'quick', name: '⚡ Quick Wins', path: '/anime/quick' },
    { id: 'statistics', name: '📊 My Stats', path: '/anime/statistics' },
    { id: 'compare', name: '👥 Compare', path: '/anime/compare' },
];

// Manga sub-tabs (under /manga)
export const MANGA_TABS = [
    { id: 'dashboard', name: '🏠 Overview', path: '/manga/dashboard' },
    { id: 'groups', name: '📚 Collections', path: '/manga/groups' },
    { id: 'related', name: '🔗 Related Manga', path: '/manga/related' },
    { id: 'discover', name: '🔍 Search', path: '/manga/discover' },
    { id: 'marathon', name: '⏳ Marathon Mode', path: '/manga/marathon' },
    { id: 'quick', name: '⚡ Quick Wins', path: '/manga/quick' },
    { id: 'statistics', name: '📊 My Stats', path: '/manga/statistics' },
    { id: 'compare', name: '👥 Compare', path: '/manga/compare' },
];

// Legacy tab config (backwards compatibility, used by old code paths)
export const TABS = [
    { id: 'dashboard', name: '🏠 Overview', path: '/dashboard' },
    { id: 'groups', name: '📚 Collections', path: '/groups' },
    { id: 'related', name: '🔗 Related Anime', path: '/related' },
    { id: 'discover', name: '🔍 Search', path: '/discover' },
    { id: 'marathon', name: '⏳ Marathon Mode', path: '/marathon' },
    { id: 'quick', name: '⚡ Quick Wins', path: '/quick' },
    { id: 'statistics', name: '📊 My Stats', path: '/statistics' },
    { id: 'compare', name: '👥 Compare', path: '/compare' },
    { id: 'settings', name: '⚙️ Settings', path: '/settings' }
];

// ===== Status Constants =====
export const ANIME_STATUS = {
    WATCHING: 'CURRENT',
    COMPLETED: 'COMPLETED',
    PLANNING: 'PLANNING',
    PAUSED: 'PAUSED',
    DROPPED: 'DROPPED',
    REPEATING: 'REPEATING'
};

export const MANGA_STATUS = {
    READING: 'CURRENT',
    COMPLETED: 'COMPLETED',
    PLANNING: 'PLANNING',
    PAUSED: 'PAUSED',
    DROPPED: 'DROPPED',
    REREADING: 'REPEATING'
};

// ===== Format Constants =====
export const ANIME_FORMATS = ['TV', 'MOVIE', 'OVA', 'ONA', 'SPECIAL', 'TV_SHORT'];
export const MANGA_FORMATS = ['MANGA', 'NOVEL', 'ONE_SHOT'];

// ===== Sorting =====
export const EPISODE_SORTING = {
    EPISODE_ASC: 'Episode Number (Ascending)',
    EPISODE_DESC: 'Episode Number (Descending)',
    TITLE_ASC: 'Title (A-Z)',
    TITLE_DESC: 'Title (Z-A)',
    RELEASE_DESC: 'Release Date (Newest First)',
    RELEASE_ASC: 'Release Date (Oldest First)',
    SCORE_DESC: 'Score (Highest First)',
    SCORE_ASC: 'Score (Lowest First)'
};

export const CHAPTER_SORTING = {
    CHAPTER_ASC: 'Chapter Number (Ascending)',
    CHAPTER_DESC: 'Chapter Number (Descending)',
    TITLE_ASC: 'Title (A-Z)',
    TITLE_DESC: 'Title (Z-A)',
    RELEASE_DESC: 'Release Date (Newest First)',
    RELEASE_ASC: 'Release Date (Oldest First)',
    SCORE_DESC: 'Score (Highest First)',
    SCORE_ASC: 'Score (Lowest First)'
};

// ===== Relation Type Exclusions (Single Source of Truth) =====
// Relation types that should NEVER be shown as "related anime/manga" suggestions.
// CHARACTER = voice actor cameos linking unrelated shows
// OTHER = studio compilations, anniversary specials
// COMPILATION = recap movies bundling existing episodes
// SUMMARY = recap/digest episodes
// ADAPTATION = manga→anime or anime→manga (not useful within same-media tab)
export const EXCLUDED_DISPLAY_RELATION_TYPES = new Set([
    'CHARACTER', 'OTHER', 'COMPILATION', 'SUMMARY', 'ADAPTATION'
]);

// Inversion map: converts "what target is TO source" → "what source is TO target"
// Used when displaying relation types from the pending anime's perspective
export const RELATION_TYPE_INVERSE = {
    'SEQUEL': 'PREQUEL',
    'PREQUEL': 'SEQUEL',
    'PARENT': 'SIDE_STORY',
    'SIDE_STORY': 'PARENT',
    'SPIN_OFF': 'PARENT',
    'ALTERNATIVE': 'ALTERNATIVE',
    'ADAPTATION': 'SOURCE',
    'SOURCE': 'ADAPTATION',
    'SUMMARY': 'PARENT',
    'COMPILATION': 'SOURCE',
    'CHARACTER': 'CHARACTER',
    'OTHER': 'OTHER',
    'RELATED': 'RELATED'
};

